
import { Component, Vue, ProvideReactive, Prop, Watch } from "vue-property-decorator"

// @ts-ignore
import draggable from "vuedraggable"

import TabButton from "./TabButton.vue"
import SortTab from "./SortTab.vue"

/**
 * @summary Component with tabs that allow tabs to be sorted
 */
@Component({
	components: {
		"tab-button": TabButton,
		draggable
	}
})
export default class SortTabs extends Vue {
	// --- Injection ---

	@ProvideReactive("tabControl") private tabsControl = this
	@ProvideReactive("activeId") private currentTabUid: string | null = null

	// --- Props ---

	@Prop({ required: true })
	value!: string | null

	@Prop({ required: true })
	sortedTabKeys!: string[]

	@Prop({ type: Boolean, default: false })
	noBody!: boolean

	// --- Data ---

	tabs: any[] = []

	public currentTab: SortTab | null = null
	dragOptions = {
		animation: 200,
		disabled: false,
		ghostClass: "ghost"
	}

	// --- Computed Properties ---

	public get activeTab () : SortTab | null {
		return this.currentTab
			? this.currentTab
			: this.tabs.length > 0
				? this.tabs[0]
				: null
	}
	public get activeId (): string | null {
		return this.currentTabUid
	}
	public set activeId (value: string | null) {
		this.currentTabUid = value
	}

	public get noSortTabs () {
		return this.tabs.filter(t => t.unsortable)
	}
	public get sortableTabs () {
		return this.tabs.filter(t => !t.unsortable).sortByMap(t => this.sortedTabKeys.indexOf(t.tabKey))
	}

	// --- Lifecycle Hooks ---

	created () {
		if (!this.tabs) {
			this.tabs = []
		}

		this.currentTab = null
		if (this.tabs.length > 0) {
			if (this.value) {
				const selectedTab = this.tabs.find(t => t.tabKey === this.value)
				if (selectedTab) {
					this.currentTab = selectedTab
					this.activeId = selectedTab.id
				}
			} else {
				this.currentTab = this.tabs[0]
				this.activeId = this.tabs[0].id
			}
		}
	}

	beforeDestroy () {
		this.activeId = null
	}

	// --- Watchers ---

	/**
	 * @summary When the value changes, ensure the value's tab is selected based on key value
	 */
	@Watch("value")
	onValueChanged (newValue: string) {
		const selectedTab = this.tabs.find(t => t.tabKey === newValue)
		if (selectedTab) {
			this.currentTab = selectedTab
			this.activeId = selectedTab.id
		}
	}

	/**
	 * @summary When a user has sorted the tab list, raise the sorted key list
	 */
	private sortComplete (sortedValue: any[]) {
		this.$emit("update:sortedTabKeys", sortedValue.map((v: any) => v.tabKey))
	}

	/**
	 * @summary Sets given tab
	 */
	private setTab (newTab: SortTab) {
		this.currentTab = newTab
		this.activeId = newTab.id

		this.$emit("input", newTab.tabKey)
	}

	/**
	 * Registers tab in list and returns tab id.
	 * @param {SortTab} tab - Sortable tab
	 * @returns {string} tab id, unique for this tab control
	 */
	public registerTab (tab: SortTab): string {
		const tabsRegistered = this.tabs.push(tab).toString()

		if (!this.currentTab) {
			this.currentTab = tab
			this.activeId = tab.tabKey
		}
		return tab.tabKey
	}

	/**
	 * @summary Removes tab from tab list
	 * @param {SortTab} tab - Sortable tab
	 */
	public unregisterTab (tab: SortTab) {
		const tabIndex = this.tabs.indexOf(tab)
		if (tabIndex > -1) {
			this.tabs.splice(tabIndex, 1)
			if (tab.id === this.activeId) {
				this.activeId = this.tabs.length > 0 ? this.tabs[0].id : null
			}
		}
	}
}

